import React from "react";
import {
  Box,
  Button,
  TextField,
  Modal,
  Typography,
  Paper,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { X } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";

export default function UserForm({
  open,
  onClose,
  editMode,
  initialValues,
  onSubmit,
  loading,
}) {

  const defaultValues = {
    fullName: "",
    email: "",
    password: "",
    role: "",
    status: "",
  };

  const formik = useFormik({
    initialValues: editMode ? initialValues : defaultValues,
    enableReinitialize: true,

    validationSchema: Yup.object({
      fullName: Yup.string().required("Full Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      password: Yup.string().when([], {
        is: () => !editMode,
        then: (schema) =>
          schema.required("Password is required").min(6, "Min 6 characters"),
        otherwise: (schema) => schema.min(6, "Min 6 characters"),
      }),
      role: Yup.string().required("Role is required"),
      status: Yup.string().required("Status is required"),
    }),

    onSubmit: (values) => {
      onSubmit(values, formik.resetForm);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper
        sx={{
          width: "90%",
          maxWidth: 450,
          p: 3.5,
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#1f2937",
            }}
          >
            {editMode ? "Edit User" : "Add New User"}
          </Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <X size={20} color="#777" />
          </IconButton>
        </Box>

        {/* Full Name */}
        <TextField
          fullWidth
          placeholder="John Doe"
          sx={{ mb: 2, borderRadius: "8px" }}
          name="fullName"
          value={formik.values.fullName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          inputProps={{ autoComplete: "new-password" }}
          error={formik.touched.fullName && Boolean(formik.errors.fullName)}
          helperText={formik.touched.fullName && formik.errors.fullName}
          disabled={loading}
        />

        {/* Email */}
        <TextField
          fullWidth
          placeholder="john@example.com"
          type="email"
          sx={{ mb: 2, borderRadius: "8px" }}
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          inputProps={{ autoComplete: "new-password" }}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          disabled={loading}
        />

        {/* Password */}
        <TextField
          fullWidth
          type="text"
          placeholder="password"
          sx={{ mb: 2, borderRadius: "8px" }}
          name="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          inputProps={{ autoComplete: "new-password" }}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          disabled={loading}
        />

        {/* Role Dropdown */}
        <Autocomplete
          disablePortal
          options={[
            // { label: "Super Admin", value: "superadmin" },
            { label: "Admin", value: "admin" },
            { label: "Account Manager", value: "accountManager" },
            { label: "Agent", value: "agent" },
            { label: "Viewer", value: "viewer" },
            // { label: "Manager", value: "manager" },
          ]}
          value={
            [
              // { label: "Super Admin", value: "superadmin" },
              { label: "Admin", value: "admin" },
              { label: "Account Manager", value: "accountManager" },
              { label: "Agent", value: "agent" },
              { label: "Viewer", value: "viewer" },
              // { label: "Manager", value: "manager" },
            ].find((option) => option.value === formik.values.role) || null
          }
          onChange={(e, newValue) => {
            formik.setFieldValue("role", newValue?.value || "");
          }}
          onBlur={() => formik.setFieldTouched("role", true)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Role"
              fullWidth
              error={formik.touched.role && Boolean(formik.errors.role)}
              helperText={formik.touched.role && formik.errors.role}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          )}
        />

        {/* Status dropdown */}
        <Autocomplete
          disablePortal
          options={[
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
            { label: "Deleted", value: "deleted" },
          ]}
          value={
            [
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
              { label: "Deleted", value: "deleted" },
            ].find((s) => s.value === formik.values.status) || null
          }
          onChange={(e, newValue) =>
            formik.setFieldValue("status", newValue?.value || "")
          }
          onBlur={() => formik.setFieldTouched("status", true)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Status"
              sx={{ mb: 3, borderRadius: "8px" }}
              error={formik.touched.status && Boolean(formik.errors.status)}
              helperText={formik.touched.status && formik.errors.status}
            />
          )}
        />

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={formik.handleSubmit}
          disabled={loading}
          sx={{
            py: 1.2,
            fontWeight: 600,
            borderRadius: "8px",
            background: "linear-gradient(135deg, #3b82f6, #2563eb)",
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} /> Processing...
            </>
          ) : editMode ? (
            "Update User"
          ) : (
            "Create User"
          )}
        </Button>
      </Paper>
    </Modal>
  );
}
